/**
 * Error handling utilities for the Student Immunization Portal
 * Provides consistent error handling, logging, and user-friendly messages
 */

import { ApiError } from '../types';

/**
 * Error codes for categorizing errors
 */
export enum ErrorCode {
  // Network errors
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT = 'TIMEOUT',
  CONNECTION_REFUSED = 'CONNECTION_REFUSED',
  
  // Authentication errors
  UNAUTHORIZED = 'UNAUTHORIZED',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  INVALID_OTP = 'INVALID_OTP',
  OTP_EXPIRED = 'OTP_EXPIRED',
  ID_VERIFICATION_FAILED = 'ID_VERIFICATION_FAILED',
  
  // Validation errors
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  INVALID_INPUT = 'INVALID_INPUT',
  MISSING_REQUIRED_FIELD = 'MISSING_REQUIRED_FIELD',
  
  // File/Upload errors
  FILE_TOO_LARGE = 'FILE_TOO_LARGE',
  INVALID_FILE_TYPE = 'INVALID_FILE_TYPE',
  UPLOAD_FAILED = 'UPLOAD_FAILED',
  OCR_FAILED = 'OCR_FAILED',
  
  // Business logic errors
  STUDENT_NOT_FOUND = 'STUDENT_NOT_FOUND',
  DUPLICATE_RECORD = 'DUPLICATE_RECORD',
  COMPLIANCE_CHECK_FAILED = 'COMPLIANCE_CHECK_FAILED',
  
  // Server errors
  SERVER_ERROR = 'SERVER_ERROR',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  
  // Unknown
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * Severity levels for errors
 */
export enum ErrorSeverity {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical',
}

/**
 * Application-specific error class
 */
export class AppError extends Error {
  public readonly code: ErrorCode;
  public readonly severity: ErrorSeverity;
  public readonly userMessage: string;
  public readonly details?: Record<string, unknown>;
  public readonly originalError?: Error;
  public readonly timestamp: Date;
  public readonly retryable: boolean;

  constructor(options: {
    code: ErrorCode;
    message: string;
    userMessage?: string;
    severity?: ErrorSeverity;
    details?: Record<string, unknown>;
    originalError?: Error;
    retryable?: boolean;
  }) {
    super(options.message);
    this.name = 'AppError';
    this.code = options.code;
    this.severity = options.severity || ErrorSeverity.ERROR;
    this.userMessage = options.userMessage || this.getDefaultUserMessage(options.code);
    this.details = options.details;
    this.originalError = options.originalError;
    this.timestamp = new Date();
    this.retryable = options.retryable ?? this.isRetryableError(options.code);
  }

  private getDefaultUserMessage(code: ErrorCode): string {
    const messages: Record<ErrorCode, string> = {
      [ErrorCode.NETWORK_ERROR]: 'Unable to connect. Please check your internet connection.',
      [ErrorCode.TIMEOUT]: 'The request took too long. Please try again.',
      [ErrorCode.CONNECTION_REFUSED]: 'Unable to reach the server. Please try again later.',
      [ErrorCode.UNAUTHORIZED]: 'Please log in to continue.',
      [ErrorCode.SESSION_EXPIRED]: 'Your session has expired. Please log in again.',
      [ErrorCode.INVALID_OTP]: 'The verification code is incorrect. Please try again.',
      [ErrorCode.OTP_EXPIRED]: 'The verification code has expired. Please request a new one.',
      [ErrorCode.ID_VERIFICATION_FAILED]: 'We could not verify your ID. Please try again or contact support.',
      [ErrorCode.VALIDATION_ERROR]: 'Please check your input and try again.',
      [ErrorCode.INVALID_INPUT]: 'The information provided is invalid.',
      [ErrorCode.MISSING_REQUIRED_FIELD]: 'Please fill in all required fields.',
      [ErrorCode.FILE_TOO_LARGE]: 'The file is too large. Please upload a smaller file.',
      [ErrorCode.INVALID_FILE_TYPE]: 'This file type is not supported.',
      [ErrorCode.UPLOAD_FAILED]: 'Failed to upload the file. Please try again.',
      [ErrorCode.OCR_FAILED]: 'Could not read the document. Please try with a clearer image.',
      [ErrorCode.STUDENT_NOT_FOUND]: 'Student record not found.',
      [ErrorCode.DUPLICATE_RECORD]: 'This record already exists.',
      [ErrorCode.COMPLIANCE_CHECK_FAILED]: 'Unable to check compliance status. Please try again.',
      [ErrorCode.SERVER_ERROR]: 'Something went wrong. Please try again later.',
      [ErrorCode.SERVICE_UNAVAILABLE]: 'The service is temporarily unavailable. Please try again later.',
      [ErrorCode.UNKNOWN_ERROR]: 'An unexpected error occurred. Please try again.',
    };
    return messages[code] || messages[ErrorCode.UNKNOWN_ERROR];
  }

  private isRetryableError(code: ErrorCode): boolean {
    const retryableCodes: ErrorCode[] = [
      ErrorCode.NETWORK_ERROR,
      ErrorCode.TIMEOUT,
      ErrorCode.CONNECTION_REFUSED,
      ErrorCode.SERVICE_UNAVAILABLE,
      ErrorCode.SERVER_ERROR,
    ];
    return retryableCodes.includes(code);
  }

  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      userMessage: this.userMessage,
      severity: this.severity,
      details: this.details,
      timestamp: this.timestamp.toISOString(),
      retryable: this.retryable,
      stack: this.stack,
    };
  }
}

/**
 * Convert various error types to AppError
 */
export const normalizeError = (error: unknown): AppError => {
  // Already an AppError
  if (error instanceof AppError) {
    return error;
  }

  // API error response
  if (isApiError(error)) {
    return new AppError({
      code: mapHttpStatusToErrorCode(error.status),
      message: error.message,
      userMessage: error.detail || error.message,
      details: { status: error.status },
    });
  }

  // Axios/fetch error
  if (isNetworkError(error)) {
    return new AppError({
      code: ErrorCode.NETWORK_ERROR,
      message: 'Network request failed',
      originalError: error instanceof Error ? error : undefined,
    });
  }

  // Standard Error
  if (error instanceof Error) {
    return new AppError({
      code: ErrorCode.UNKNOWN_ERROR,
      message: error.message,
      originalError: error,
    });
  }

  // String error
  if (typeof error === 'string') {
    return new AppError({
      code: ErrorCode.UNKNOWN_ERROR,
      message: error,
    });
  }

  // Unknown error type
  return new AppError({
    code: ErrorCode.UNKNOWN_ERROR,
    message: 'An unexpected error occurred',
    details: { originalError: String(error) },
  });
};

/**
 * Check if error is an API error response
 */
export const isApiError = (error: unknown): error is ApiError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    'message' in error
  );
};

/**
 * Check if error is a network error
 */
export const isNetworkError = (error: unknown): boolean => {
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }
  if (typeof error === 'object' && error !== null) {
    const err = error as Record<string, unknown>;
    return err.code === 'ECONNREFUSED' || err.code === 'ENOTFOUND' || err.name === 'NetworkError';
  }
  return false;
};

/**
 * Map HTTP status codes to error codes
 */
export const mapHttpStatusToErrorCode = (status: number): ErrorCode => {
  const statusMap: Record<number, ErrorCode> = {
    400: ErrorCode.VALIDATION_ERROR,
    401: ErrorCode.UNAUTHORIZED,
    403: ErrorCode.UNAUTHORIZED,
    404: ErrorCode.STUDENT_NOT_FOUND,
    408: ErrorCode.TIMEOUT,
    409: ErrorCode.DUPLICATE_RECORD,
    413: ErrorCode.FILE_TOO_LARGE,
    415: ErrorCode.INVALID_FILE_TYPE,
    422: ErrorCode.VALIDATION_ERROR,
    429: ErrorCode.SERVICE_UNAVAILABLE,
    500: ErrorCode.SERVER_ERROR,
    502: ErrorCode.SERVICE_UNAVAILABLE,
    503: ErrorCode.SERVICE_UNAVAILABLE,
    504: ErrorCode.TIMEOUT,
  };
  return statusMap[status] || ErrorCode.UNKNOWN_ERROR;
};

/**
 * Error logging interface
 */
export interface ErrorLogger {
  log: (error: AppError) => void;
  warn: (error: AppError) => void;
  error: (error: AppError) => void;
  critical: (error: AppError) => void;
}

/**
 * Default console logger
 */
export const consoleLogger: ErrorLogger = {
  log: (error: AppError) => {
    console.log('[INFO]', error.toJSON());
  },
  warn: (error: AppError) => {
    console.warn('[WARN]', error.toJSON());
  },
  error: (error: AppError) => {
    console.error('[ERROR]', error.toJSON());
  },
  critical: (error: AppError) => {
    console.error('[CRITICAL]', error.toJSON());
  },
};

/**
 * Current logger instance (can be replaced for production)
 */
let currentLogger: ErrorLogger = consoleLogger;

/**
 * Set the error logger
 */
export const setErrorLogger = (logger: ErrorLogger): void => {
  currentLogger = logger;
};

/**
 * Log an error based on its severity
 */
export const logError = (error: AppError): void => {
  switch (error.severity) {
    case ErrorSeverity.INFO:
      currentLogger.log(error);
      break;
    case ErrorSeverity.WARNING:
      currentLogger.warn(error);
      break;
    case ErrorSeverity.ERROR:
      currentLogger.error(error);
      break;
    case ErrorSeverity.CRITICAL:
      currentLogger.critical(error);
      break;
  }
};

/**
 * Handle an error with logging and optional callback
 */
export const handleError = (
  error: unknown,
  options?: {
    onError?: (error: AppError) => void;
    showToUser?: boolean;
    rethrow?: boolean;
  }
): AppError => {
  const appError = normalizeError(error);
  
  // Log the error
  logError(appError);

  // Call optional callback
  if (options?.onError) {
    options.onError(appError);
  }

  // Rethrow if requested
  if (options?.rethrow) {
    throw appError;
  }

  return appError;
};

/**
 * Create an error boundary handler for React components
 */
export const createErrorBoundaryHandler = (
  componentName: string
): ((error: Error, errorInfo: { componentStack: string }) => void) => {
  return (error: Error, errorInfo: { componentStack: string }) => {
    const appError = new AppError({
      code: ErrorCode.UNKNOWN_ERROR,
      message: `Error in ${componentName}: ${error.message}`,
      severity: ErrorSeverity.CRITICAL,
      originalError: error,
      details: {
        componentName,
        componentStack: errorInfo.componentStack,
      },
    });
    
    logError(appError);
  };
};

/**
 * Retry utility for failed operations
 */
export const withRetry = async <T>(
  operation: () => Promise<T>,
  options?: {
    maxAttempts?: number;
    delayMs?: number;
    backoff?: boolean;
    shouldRetry?: (error: AppError) => boolean;
  }
): Promise<T> => {
  const {
    maxAttempts = 3,
    delayMs = 1000,
    backoff = true,
    shouldRetry = (error: AppError) => error.retryable,
  } = options || {};

  let lastError: AppError | undefined;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = normalizeError(error);
      
      if (attempt === maxAttempts || !shouldRetry(lastError)) {
        throw lastError;
      }
      
      const delay = backoff ? delayMs * attempt : delayMs;
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError || new AppError({
    code: ErrorCode.UNKNOWN_ERROR,
    message: 'All retry attempts failed',
  });
};

/**
 * User-friendly error message helper
 */
export const getUserFriendlyMessage = (
  error: unknown,
  fallback: string = 'Something went wrong. Please try again.'
): string => {
  const appError = normalizeError(error);
  return appError.userMessage || fallback;
};

/**
 * Check if an error should trigger a logout
 */
export const shouldLogout = (error: unknown): boolean => {
  const appError = normalizeError(error);
  return [ErrorCode.UNAUTHORIZED, ErrorCode.SESSION_EXPIRED].includes(appError.code);
};

/**
 * Format error for display
 */
export const formatErrorForDisplay = (
  error: unknown,
  locale: string = 'en'
): { title: string; message: string; showRetry: boolean } => {
  const appError = normalizeError(error);

  // Localized titles could be added here
  const titles: Record<ErrorCode, string> = {
    [ErrorCode.NETWORK_ERROR]: 'Connection Problem',
    [ErrorCode.TIMEOUT]: 'Request Timeout',
    [ErrorCode.CONNECTION_REFUSED]: 'Server Unavailable',
    [ErrorCode.UNAUTHORIZED]: 'Authentication Required',
    [ErrorCode.SESSION_EXPIRED]: 'Session Expired',
    [ErrorCode.INVALID_OTP]: 'Invalid Code',
    [ErrorCode.OTP_EXPIRED]: 'Code Expired',
    [ErrorCode.ID_VERIFICATION_FAILED]: 'Verification Failed',
    [ErrorCode.VALIDATION_ERROR]: 'Invalid Input',
    [ErrorCode.INVALID_INPUT]: 'Invalid Input',
    [ErrorCode.MISSING_REQUIRED_FIELD]: 'Missing Information',
    [ErrorCode.FILE_TOO_LARGE]: 'File Too Large',
    [ErrorCode.INVALID_FILE_TYPE]: 'Invalid File Type',
    [ErrorCode.UPLOAD_FAILED]: 'Upload Failed',
    [ErrorCode.OCR_FAILED]: 'Document Reading Failed',
    [ErrorCode.STUDENT_NOT_FOUND]: 'Not Found',
    [ErrorCode.DUPLICATE_RECORD]: 'Duplicate Entry',
    [ErrorCode.COMPLIANCE_CHECK_FAILED]: 'Check Failed',
    [ErrorCode.SERVER_ERROR]: 'Server Error',
    [ErrorCode.SERVICE_UNAVAILABLE]: 'Service Unavailable',
    [ErrorCode.UNKNOWN_ERROR]: 'Error',
  };

  return {
    title: titles[appError.code] || 'Error',
    message: appError.userMessage,
    showRetry: appError.retryable,
  };
};